import React from 'react';
import MailComposer from './MailComposer';
import { modeContract } from './mailComposerModes';

/**
 * ReplyComposer — the thread's reply card, a thin wrapper over MailComposer.
 *
 * Used by both the unified inbox (`EmailDetailPage`) and the personal inbox
 * (`PersonalThreadView`). Its own job is the card's chrome and the `reply` /
 * `reply_all` contract; everything inside — recipients, the template/signature
 * slot, the editor, attachments, the send row — is `MailComposer`.
 *
 * ## Two placements, chosen by the mode contract (#659)
 *
 *  - `reply` is ANCHORED: the lane renders it in the replied-to message's
 *    `ThreadMessage.composer` slot, and this card is inset under that message
 *    (`margin: 0 28px 22px 74px`, border `#D9D5F5`, a white card whose header
 *    row alone is `#FBFAFE`). The indent is what makes "this reply belongs to
 *    that message" legible. Its recipients are implicit: there is no To row,
 *    so the lane must resolve them first (`inboxRules.resolveReplyTarget`).
 *  - `reply_all` is NOT anchored: the lane renders it once, after the last
 *    message, and it carries `data-testid="thread-level-composer"`. It is a
 *    NEW thread: the lanes send it through their compose endpoint, not the
 *    reply one, under the read-only `subject` line. The
 *    container class lives HERE, keyed on the same contract, so the two lanes
 *    cannot drift apart on it.
 *
 * ## No identity, no mode toggle
 *
 * The From choice is the reader footer's "Replying as" (`ThreadFooter`, #659
 * D3), and the Reply ↔ Reply All toggle is gone (D4): Reply all opens from the
 * footer or the `⋯` menu. So this card takes no identity props at all.
 *
 * Props:
 *  - subject: reply_all's read-only subject line, from `threadSubject` — the
 *    SAME value the lane sends, so what is shown is what goes out. Ignored by
 *    `reply`, which has no subject row.
 *  - modeVars: fills `{name}` / `{subject}` / `{n}` in the mode header. `name`
 *    must come from `resolveReplyTarget`, never the row's sender, or a reply to
 *    our own message would read "Reply to <me>".
 *  - sendDisabled: blocks Send while the caller cannot yet route it — the
 *    personal lane sets this while the mailbox-status queries are in flight,
 *    because until they resolve the send would silently fall through to
 *    log-only (#484).
 *  - onClose: the ✕ in the header, and Discard in the send row (the same
 *    close-and-clear; the prototype's Discard IS `closeReply`).
 *  - templates / adminTemplates / signatures / adminSignatures /
 *    activeOverrideId / user / senderEmail / signature / onSignatureChange
 *    (#597): the template/signature slot's data, passed straight through. This
 *    component fetches NOTHING — it renders inside the thread's `.map(...)`, so
 *    a data hook here would break the rules of hooks (an `error` in
 *    craco.config.js, therefore a `yarn build` failure) and multiply each
 *    hook's mount-time invalidateQueries by the thread length. Both callers
 *    call the hooks once, at their own top level.
 *  - senderEmail is the mailbox actually sending: it feeds {{SenderEmail}},
 *    and a change to it re-resolves the inserted signature (F8, in
 *    `TemplateSignatureSelectors`). On the personal lane it is NOT the
 *    footer's option value, which is a provider id; a provider id merged into
 *    {{SenderEmail}} would go out in the body of a real message.
 *
 * Every historical `data-testid` that still has a control behind it is kept
 * through `MailComposer`'s `testIds` map (`reply-composer-{id}`,
 * `reply-composer-close-{id}`, `inbox-reply-send-{id}`), so the assertions
 * written against them keep testing the thing they were written for.
 */
const ANCHORED_CARD = 'mx-[28px] mb-[22px] ml-[74px] rounded-[10px] '
    + 'border border-[#D9D5F5] bg-white overflow-hidden';
const THREAD_CARD = 'mt-[22px] mx-[28px] mb-2 ml-[74px] rounded-[10px] '
    + 'border border-[#E4E1DB] bg-white overflow-hidden';

export default function ReplyComposer({
    composerId,
    sendDisabled = false,
    mode,
    modeVars,
    subject,
    to,
    setTo,
    cc,
    setCc,
    bcc,
    setBcc,
    showCcBcc,
    setShowCcBcc,
    body,
    setBody,
    reconnectRequired,
    reply,
    onSubmit,
    onClose,
    editorRef,
    // #597 selector data — all optional; a caller that passes none renders
    // exactly the pre-#597 composer.
    templates,
    adminTemplates,
    signatures,
    adminSignatures,
    activeOverrideId,
    user,
    senderEmail,
    signature,
    onSignatureChange,
    selectorsDisabled = false,
    // #623 attachments — optional for the same reason.
    attachments,
    onAddAttachments,
    onRemoveAttachment,
    // Where the reconnect banner sends the user (#487). Defaults to the
    // departmental unified-inbox admin page, this component's original caller.
    // The PERSONAL inbox must override it: /admin/unified-inbox is admin-gated
    // (a non-admin bounces to /dashboard) and manages department mailboxes, so
    // a personal-mailbox user sent there cannot fix anything. The backends' own
    // 401s say "Settings → Integrations".
    reconnectHref = '/admin/unified-inbox',
}) {
    const anchored = modeContract(mode).anchored;
    const composer = (
        <MailComposer
            mode={mode}
            modeVars={modeVars}
            composerId={composerId}
            variant={anchored ? 'card' : 'thread'}
            className={anchored ? ANCHORED_CARD : THREAD_CARD}
            testIds={{
                'mail-composer': `reply-composer-${composerId}`,
                'reply-composer-close': `reply-composer-close-${composerId}`,
                'inbox-reply-send': `inbox-reply-send-${composerId}`,
            }}
            to={to}
            onToChange={setTo}
            cc={cc}
            onCcChange={setCc}
            bcc={bcc}
            onBccChange={setBcc}
            subject={subject}
            showCcBcc={showCcBcc}
            onToggleCcBcc={value => setShowCcBcc(value)}
            body={body}
            onBodyChange={setBody}
            editorRef={editorRef}
            templates={templates}
            adminTemplates={adminTemplates}
            signatures={signatures}
            adminSignatures={adminSignatures}
            activeOverrideId={activeOverrideId}
            user={user}
            senderEmail={senderEmail}
            signature={signature}
            onSignatureChange={onSignatureChange}
            selectorsDisabled={selectorsDisabled}
            attachments={attachments}
            onAddAttachments={onAddAttachments}
            onRemoveAttachment={onRemoveAttachment}
            onClose={onClose}
            onDiscard={onClose}
            onSend={onSubmit}
            // `reply.isPending` is the mutation's own flag; kept as the single
            // source of "a send is in flight" rather than a second local state.
            sending={Boolean(reply?.isPending)}
            sendDisabled={sendDisabled}
            reconnectRequired={reconnectRequired}
            reconnectHref={reconnectHref}
        />
    );
    return anchored ? composer : <div data-testid="thread-level-composer">{composer}</div>;
}
